import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useAuth } from '../../contexts/AuthContext';
import {
  buildEmptyGruppenpruefungResult,
  getGroupPracticalStationConfig,
  getGroupPracticalGrade,
  getGroupPracticalGradeLabel,
  getGroupPracticalOverallPoints,
  getGroupPracticalTimePoints,
  formatDuration,
  GRUPPENPRUEFUNG_CUSTOM_ACTION_ID,
  getGruppenpruefungActionAverage,
  getGruppenpruefungAverageTimeMs,
  GRUPPENPRUEFUNG_POINT_OPTIONS,
  GRUPPENPRUEFUNG_PARTICIPANT_POSITIONS,
  isGroupPracticalFailed,
  parseMmSsToMs,
  roundToSecondMs,
} from './srhtExamPracticalSheets';

function getSelectableUserId(user) {
  return Number(user?.userId || user?.id || 0);
}

function getSelectableUserName(user) {
  return user?.fullName || '-';
}

function getGradeChipColor(grade, failed = false) {
  if (failed) return 'error';
  if (grade === 1 || grade === 2) return 'success';
  if (grade === 3 || grade === 4) return 'warning';
  if (grade === 5 || grade === 6) return 'error';
  return 'default';
}

function getReadableDisabledFieldSx() {
  return {
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: (theme) => theme.palette.text.primary,
    },
    '& .MuiSelect-select.Mui-disabled': {
      WebkitTextFillColor: (theme) => theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-disabled': {
      color: (theme) => theme.palette.text.secondary,
    },
  };
}

const GROUP_TIMER_SESSION_KEY = 'group_timer';

function getGruppenpruefungPointLabel(points) {
  const matchingOption = GRUPPENPRUEFUNG_POINT_OPTIONS.find((option) => option.points === Number(points));
  return matchingOption ? `${matchingOption.points} - ${matchingOption.label}` : String(points);
}

function formatCandidateTitleName(fullName) {
  const normalizedName = String(fullName || '').trim();
  if (!normalizedName) {
    return 'Unbekannter Teilnehmer';
  }

  const parts = normalizedName.split(/\s+/).filter(Boolean);
  if (parts.length < 2) {
    return normalizedName;
  }

  const lastName = parts[parts.length - 1];
  const firstNames = parts.slice(0, -1).join(' ');
  return `${lastName}, ${firstNames}`;
}

function formatGroupGradeRangeLabel(range) {
  const normalizedMin = Number.isInteger(range?.min) ? range.min : Math.ceil(Number(range?.min || 0));
  const normalizedMax = Number.isInteger(range?.max) ? range.max : Math.floor(Number(range?.max || 0));
  if (Number(range?.grade) === 1) {
    return `${range.label}: ${normalizedMax} bis ${normalizedMin} Punkte`;
  }

  return `${range.label}: unter ${normalizedMax + 1} bis ${normalizedMin} Punkte`;
}

export default function SrhtExamGroupPracticalPanel({
  stationCode,
  stationState,
  candidates = [],
  examiners = [],
  sheetRecord = null,
  isSheetFinalized = false,
  canEdit,
  isOffline = false,
  actionBusy = '',
  onChangeStation,
  onSyncTimerSession,
  onSave,
  onFinalize,
  onUnlock,
}) {
  const { user } = useAuth();
  const stationConfig = useMemo(() => getGroupPracticalStationConfig(stationCode), [stationCode]);
  const [timerTick, setTimerTick] = useState(Date.now());
  const [groupTimerState, setGroupTimerState] = useState({ running: false, startReference: null });
  const [unlockDialog, setUnlockDialog] = useState({ open: false, reason: '', candidateId: '', reopenGroupTime: false });
  const [finalizeTimeDialog, setFinalizeTimeDialog] = useState({ open: false, candidateId: '' });
  const [safetyQuestionDialogOpen, setSafetyQuestionDialogOpen] = useState(false);
  const [contributionDraft, setContributionDraft] = useState({
    actionId: '',
    participantCandidateId: '',
    points: '',
    notes: '',
    examinerUserId: '',
  });
  const [customActionLabel, setCustomActionLabel] = useState('');
  const [manualTimeDialog, setManualTimeDialog] = useState({ open: false, value: '00:00', error: '' });
  const [localNotice, setLocalNotice] = useState(null);
  const [groupAssignmentDialog, setGroupAssignmentDialog] = useState({
    open: false,
    leaderExaminerUserId: '',
    participants: [],
  });
  const [safetyIncidentDialog, setSafetyIncidentDialog] = useState({ open: false, description: '' });
  const [contributionEditorDialog, setContributionEditorDialog] = useState({
    open: false,
    candidateId: '',
    entries: [],
  });
  const readableTextFieldProps = {
    InputLabelProps: { shrink: true },
    sx: getReadableDisabledFieldSx(),
  };

  const candidateById = useMemo(
    () => new Map(candidates.map((candidate) => [String(candidate.id), candidate])),
    [candidates],
  );
  const examinerById = useMemo(
    () => new Map(examiners.map((examiner) => [String(getSelectableUserId(examiner)), examiner])),
    [examiners],
  );
  const defaultExaminerUserId = useMemo(() => {
    const currentUserId = String(user?.id || '');
    if (currentUserId && examinerById.has(currentUserId)) {
      return currentUserId;
    }
    return String(getSelectableUserId(examiners[0]) || '');
  }, [examinerById, examiners, user?.id]);

  const groups = Array.isArray(stationState?.groups) ? stationState.groups : [];
  const selectedGroupId = stationState?.selectedGroupId || groups[0]?.id || '';
  const selectedGroup = groups.find((group) => String(group.id) === String(selectedGroupId)) || groups[0] || null;
  const selectedGroupParticipants = useMemo(
    () => (Array.isArray(selectedGroup?.participants) ? selectedGroup.participants : []),
    [selectedGroup?.participants],
  );
  const selectedCandidateId = stationState?.selectedCandidateId
    || selectedGroupParticipants.find((participant) => participant.candidateId && !participant.outOfCompetition)?.candidateId
    || selectedGroupParticipants.find((participant) => participant.candidateId)?.candidateId
    || '';
  const canEditGroup = Boolean(canEdit && !isOffline);

  useEffect(() => {
    const validParticipantIds = new Set(
      selectedGroupParticipants
        .filter((participant) => participant?.candidateId && participant?.outOfCompetition !== true)
        .map((participant) => String(participant.candidateId)),
    );

    setContributionDraft((current) => (
      current.participantCandidateId && !validParticipantIds.has(String(current.participantCandidateId))
        ? { ...current, participantCandidateId: '' }
        : current
    ));
  }, [selectedGroupParticipants]);

  useEffect(() => {
    if (!selectedGroup || !selectedCandidateId || !onChangeStation) {
      return;
    }

    if (stationState?.selectedGroupId === selectedGroup.id && stationState?.selectedCandidateId === String(selectedCandidateId)) {
      return;
    }

    onChangeStation({
      ...stationState,
      selectedGroupId: selectedGroup.id,
      selectedCandidateId: String(selectedCandidateId),
    });
  }, [onChangeStation, selectedCandidateId, selectedGroup, stationState]);

  useEffect(() => {
    const runningTimer = sheetRecord?.timerSession?.[GROUP_TIMER_SESSION_KEY];
    if (!runningTimer?.running || !runningTimer?.startReference) {
      setGroupTimerState({ running: false, startReference: null });
      return;
    }

    setGroupTimerState({
      running: true,
      startReference: Number(runningTimer.startReference),
    });
    setTimerTick(Date.now());
  }, [sheetRecord?.timerSession]);

  useEffect(() => {
    if (!groupTimerState.running || !groupTimerState.startReference) {
      return undefined;
    }

    const intervalId = window.setInterval(() => setTimerTick(Date.now()), 250);
    return () => window.clearInterval(intervalId);
  }, [groupTimerState]);

  useEffect(() => {
    setContributionDraft((current) => ({
      ...current,
      examinerUserId: current.examinerUserId || defaultExaminerUserId,
    }));
  }, [defaultExaminerUserId]);

  useEffect(() => {
    if (!localNotice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setLocalNotice(null), localNotice.severity === 'success' ? 2500 : 4000);
    return () => window.clearTimeout(timeoutId);
  }, [localNotice]);

  const updateStation = (updater) => {
    if (!onChangeStation) {
      return null;
    }

    const nextStation = typeof updater === 'function' ? updater(stationState) : updater;
    onChangeStation(nextStation);
    return nextStation;
  };

  const updateGroup = (groupId, updater) => updateStation((current) => ({
    ...current,
    groups: (current.groups || []).map((group) => (
      String(group.id) === String(groupId)
        ? (typeof updater === 'function' ? updater(group) : updater)
        : group
    )),
  }));

  const persistStationChange = async (nextStation, options = {}) => {
    if (!onSave || !stationCode || !nextStation) {
      return;
    }

    await onSave(stationCode, nextStation, options);
  };

  const buildNextStationWithGroupPatch = (groupId, nextGroup) => ({
    ...(stationState || {}),
    groups: (stationState?.groups || []).map((group) => (
      String(group.id) === String(groupId)
        ? nextGroup
        : group
    )),
  });

  const buildNextStationWithUpdatedGroup = (groupUpdater) => ({
    ...(stationState || {}),
    groups: (stationState?.groups || []).map((group) => (
      String(group.id) === String(selectedGroup?.id)
        ? groupUpdater(group)
        : group
    )),
  });

  const serializeGroupTimerSession = (nextTimerState) => (
    nextTimerState?.running && nextTimerState?.startReference
      ? {
        [GROUP_TIMER_SESSION_KEY]: {
          running: true,
          startReference: Math.round(Number(nextTimerState.startReference)),
        },
      }
      : null
  );

  const commitGroupTimerMutation = async (nextStation, nextTimerState) => {
    if (!onSyncTimerSession || !stationCode) {
      updateStation(nextStation);
      setGroupTimerState(nextTimerState);
      setTimerTick(Date.now());
      return true;
    }

    try {
      await onSyncTimerSession(stationCode, nextStation, serializeGroupTimerSession(nextTimerState));
      updateStation(nextStation);
      setGroupTimerState(nextTimerState);
      setTimerTick(Date.now());
      return true;
    } catch {
      return false;
    }
  };

  const handleOpenGroupAssignmentDialog = () => {
    if (!selectedGroup) {
      return;
    }

    setGroupAssignmentDialog({
      open: true,
      leaderExaminerUserId: String(selectedGroup.leaderExaminerUserId || ''),
      participants: (selectedGroupParticipants || []).map((participant) => ({ ...participant })),
    });
  };

  const handleCloseGroupAssignmentDialog = () => {
    setGroupAssignmentDialog({ open: false, leaderExaminerUserId: '', participants: [] });
  };

  const handleGroupAssignmentParticipantChange = (slotId, patch) => {
    setGroupAssignmentDialog((current) => ({
      ...current,
      participants: (current.participants || []).map((participant) => (
        String(participant.slotId) === String(slotId)
          ? { ...participant, ...patch }
          : participant
      )),
    }));
  };

  const handleSaveGroupAssignmentDialog = async () => {
    if (!selectedGroup) {
      return;
    }

    const nextGroup = {
      ...selectedGroup,
      leaderExaminerUserId: Number(groupAssignmentDialog.leaderExaminerUserId) || '',
      participants: (groupAssignmentDialog.participants || []).map((participant) => ({ ...participant })),
    };
    const nextStation = buildNextStationWithGroupPatch(selectedGroup.id, nextGroup);
    updateStation(nextStation);
    await persistStationChange(nextStation, { silent: true });
    handleCloseGroupAssignmentDialog();
    setLocalNotice({ severity: 'success', message: 'Gruppenbesetzung wurde gespeichert.' });
  };

  const handleStartTimer = async () => {
    if (!canEditGroup || !selectedGroup || groupTimerState.running || selectedGroup?.timeLock?.isFinal) {
      return;
    }

    await commitGroupTimerMutation(stationState, {
      running: true,
      startReference: Date.now(),
    });
  };

  const handleStopTimer = async () => {
    if (!groupTimerState.running || !groupTimerState.startReference || !selectedGroup || selectedGroup?.timeLock?.isFinal) {
      return;
    }

    const elapsedMs = Date.now() - groupTimerState.startReference;
    const nextStation = buildNextStationWithUpdatedGroup((group) => ({
      ...group,
      timeEntries: [{
        id: `time_${group.id}`,
        groupId: group.id,
        examinerUserId: Number(group.leaderExaminerUserId || defaultExaminerUserId || user?.id || 0),
        totalMs: roundToSecondMs(elapsedMs),
        recordedAt: new Date().toISOString(),
        sequence: 1,
      }],
    }));

    await commitGroupTimerMutation(nextStation, { running: false, startReference: null });
  };

  const openManualTimeDialog = () => {
    if (!selectedGroup || selectedGroup?.timeLock?.isFinal) {
      return;
    }

    setManualTimeDialog({
      open: true,
      value: formatDuration(Number((selectedGroup.timeEntries || [])[0]?.totalMs || 0)),
      error: '',
    });
  };

  const closeManualTimeDialog = () => {
    setManualTimeDialog({ open: false, value: '00:00', error: '' });
  };

  const confirmManualTimeDialog = async () => {
    if (!selectedGroup) {
      return;
    }

    const parsedValue = parseMmSsToMs(manualTimeDialog.value || '');
    if (parsedValue == null) {
      setManualTimeDialog((current) => ({ ...current, error: 'Bitte eine Zeit im Format MM:SS eingeben.' }));
      return;
    }

    const nextStation = buildNextStationWithUpdatedGroup((group) => ({
      ...group,
      timeEntries: [{
        id: `time_${group.id}`,
        groupId: group.id,
        examinerUserId: Number(group.leaderExaminerUserId || defaultExaminerUserId || user?.id || 0),
        totalMs: roundToSecondMs(parsedValue),
        recordedAt: new Date().toISOString(),
        sequence: 1,
      }],
    }));

    const committed = await commitGroupTimerMutation(nextStation, { running: false, startReference: null });
    if (committed) {
      closeManualTimeDialog();
    }
  };

  const handleResetGroupTime = async () => {
    if (!selectedGroup || selectedGroup?.timeLock?.isFinal) {
      return;
    }

    const nextStation = buildNextStationWithUpdatedGroup((group) => ({
      ...group,
      timeEntries: [],
    }));

    const committed = await commitGroupTimerMutation(nextStation, { running: false, startReference: null });
    if (committed) {
      closeManualTimeDialog();
    }
  };

  const handleAddContribution = async () => {
    if (!selectedGroup || !contributionDraft.examinerUserId || !contributionDraft.participantCandidateId || !contributionDraft.actionId || contributionDraft.points === '') {
      return;
    }
    if (contributionDraft.actionId === GRUPPENPRUEFUNG_CUSTOM_ACTION_ID && !customActionLabel.trim()) {
      setLocalNotice({ severity: 'warning', message: 'Bitte zuerst eine Bezeichnung für die individuelle Handlung eingeben.' });
      return;
    }
    const targetedResult = stationState?.resultsByCandidateId?.[String(contributionDraft.participantCandidateId)] || null;
    if (String(targetedResult?.status || '').trim() === 'finalized') {
      setLocalNotice({ severity: 'warning', message: 'Finalisierte Teilnehmer müssen vor Änderungen zuerst entsperrt werden.' });
      return;
    }

    const nextSequence = Number(selectedGroup.nextContributionSequence || 1);
    const createdCustomActionId = contributionDraft.actionId === GRUPPENPRUEFUNG_CUSTOM_ACTION_ID
      ? `custom_${selectedGroup.id}_${Date.now()}`
      : contributionDraft.actionId;
    const nextStation = updateGroup(selectedGroup.id, (group) => ({
      ...group,
      actionCatalog: contributionDraft.actionId === GRUPPENPRUEFUNG_CUSTOM_ACTION_ID && customActionLabel.trim()
        ? [
          ...(group.actionCatalog || []),
          {
            id: createdCustomActionId,
            order: 100 + (group.actionCatalog || []).filter((action) => action?.isCustom).length,
            label: customActionLabel.trim(),
            isCustom: true,
          },
        ]
        : (group.actionCatalog || []),
      contributionEntries: [
        ...(group.contributionEntries || []),
        {
          id: `contribution_${group.id}_${nextSequence}`,
          groupId: group.id,
          participantCandidateId: String(contributionDraft.participantCandidateId),
          examinerUserId: Number(contributionDraft.examinerUserId),
          actionId: createdCustomActionId,
          points: Number(contributionDraft.points),
          notes: contributionDraft.notes || '',
          sequence: nextSequence,
          updatedAt: new Date().toISOString(),
        },
      ],
      nextContributionSequence: nextSequence + 1,
    }));

    if (nextStation) {
      await persistStationChange(nextStation, { silent: true });
    }

    setContributionDraft((current) => ({
      ...current,
      actionId: '',
      points: '',
      notes: '',
    }));
    setCustomActionLabel('');
    setLocalNotice({ severity: 'success', message: 'Zwischenbewertung wurde hinzugefügt.' });
  };

  const handleOpenContributionEditorDialog = (candidateId) => {
    const entries = (selectedGroup?.contributionEntries || [])
      .filter((entry) => String(entry.participantCandidateId || '') === String(candidateId || ''))
      .map((entry) => ({
        ...entry,
        examinerUserId: String(entry.examinerUserId || ''),
        points: String(entry.points ?? ''),
      }));

    setContributionEditorDialog({
      open: true,
      candidateId: String(candidateId || ''),
      entries,
    });
  };

  const handleCloseContributionEditorDialog = () => {
    setContributionEditorDialog({ open: false, candidateId: '', entries: [] });
  };

  const handleContributionEditorEntryChange = (entryId, patch) => {
    setContributionEditorDialog((current) => ({
      ...current,
      entries: (current.entries || []).map((entry) => (
        String(entry.id) === String(entryId)
          ? { ...entry, ...patch }
          : entry
      )),
    }));
  };

  const handleContributionEditorDeleteEntry = (entryId) => {
    setContributionEditorDialog((current) => ({
      ...current,
      entries: (current.entries || []).filter((entry) => String(entry.id) !== String(entryId)),
    }));
  };

  const handleSaveContributionEditorDialog = async () => {
    if (!selectedGroup || !contributionEditorDialog.candidateId) {
      return;
    }

    const invalidEntry = (contributionEditorDialog.entries || []).some(
      (entry) => !entry.examinerUserId || !entry.actionId || entry.points === '',
    );
    if (invalidEntry) {
      setLocalNotice({ severity: 'warning', message: 'Bitte alle Felder der Zwischenbewertungen ausfüllen.' });
      return;
    }

    const targetedResult = stationState?.resultsByCandidateId?.[String(contributionEditorDialog.candidateId)] || null;
    if (String(targetedResult?.status || '').trim() === 'finalized') {
      setLocalNotice({ severity: 'warning', message: 'Finalisierte Teilnehmer müssen vor Änderungen zuerst entsperrt werden.' });
      return;
    }

    const normalizedEntries = (contributionEditorDialog.entries || []).map((entry, index) => ({
      ...entry,
      participantCandidateId: String(contributionEditorDialog.candidateId),
      examinerUserId: Number(entry.examinerUserId),
      points: Number(entry.points),
      sequence: Number.isFinite(Number(entry.sequence)) && Number(entry.sequence) > 0 ? Number(entry.sequence) : (index + 1),
      updatedAt: new Date().toISOString(),
    }));

    const nextStation = updateGroup(selectedGroup.id, (group) => ({
      ...group,
      contributionEntries: [
        ...(group.contributionEntries || []).filter(
          (entry) => String(entry.participantCandidateId || '') !== String(contributionEditorDialog.candidateId),
        ),
        ...normalizedEntries,
      ],
    }));
    if (nextStation) {
      await persistStationChange(nextStation, { silent: true });
    }

    handleCloseContributionEditorDialog();
    setLocalNotice({ severity: 'success', message: 'Zwischenbewertungen wurden gespeichert.' });
  };

  const handleOpenSafetyIncidentDialog = () => {
    setSafetyIncidentDialog({ open: true, description: selectedGroup?.safetyIncident?.description || '' });
  };

  const handleCloseSafetyIncidentDialog = () => {
    setSafetyIncidentDialog({ open: false, description: '' });
  };

  const handleSaveSafetyIncidentNo = async () => {
    if (!selectedGroup) {
      return;
    }

    const nextStation = updateGroup(selectedGroup.id, (group) => ({
      ...group,
      safetyIncident: {
        ...(group.safetyIncident || {}),
        answered: true,
        hasIncident: false,
        description: '',
      },
    }));
    if (nextStation) {
      await persistStationChange(nextStation, { silent: true });
    }
    setLocalNotice({ severity: 'success', message: 'Sicherheitsstatus wurde gespeichert.' });
  };

  const handleSaveSafetyIncidentYes = async () => {
    if (!selectedGroup) {
      return;
    }

    if (!String(safetyIncidentDialog.description || '').trim()) {
      setLocalNotice({ severity: 'warning', message: 'Bitte eine Beschreibung zum sicherheitsrelevanten Fehler eingeben.' });
      return;
    }

    const nextStation = updateGroup(selectedGroup.id, (group) => ({
      ...group,
      safetyIncident: {
        ...(group.safetyIncident || {}),
        answered: true,
        hasIncident: true,
        description: String(safetyIncidentDialog.description || '').trim(),
      },
    }));
    if (nextStation) {
      await persistStationChange(nextStation, { silent: true });
    }
    handleCloseSafetyIncidentDialog();
    setLocalNotice({ severity: 'success', message: 'Sicherheitsrelevanter Fehler wurde gespeichert.' });
  };

  const handleFinalizeCandidate = async (candidateId, options = {}) => {
    if (!onFinalize || !candidateId || !stationCode) {
      return;
    }

    await onFinalize(stationCode, stationState, Number(candidateId), options);
  };

  const requestFinalizeCandidate = async (candidateId) => {
    const result = stationState?.resultsByCandidateId?.[String(candidateId)] || null;
    if (String(result?.status || '').trim() === 'finalized') {
      return;
    }

    if (selectedGroup?.safetyIncident?.answered !== true) {
      setLocalNotice({ severity: 'warning', message: 'Vor dem Finalisieren muss zuerst die Frage nach einem sicherheitsrelevanten Fehler mit Ja oder Nein beantwortet werden.' });
      setSafetyQuestionDialogOpen(true);
      return;
    }

    if (selectedGroup?.timeLock?.isFinal) {
      await handleFinalizeCandidate(candidateId);
      return;
    }

    setFinalizeTimeDialog({ open: true, candidateId: String(candidateId) });
  };

  const handleConfirmFinalTimeAndFinalize = async () => {
    if (!finalizeTimeDialog.candidateId) {
      return;
    }

    await handleFinalizeCandidate(finalizeTimeDialog.candidateId, { confirmGroupTimeFinal: true });
    setFinalizeTimeDialog({ open: false, candidateId: '' });
  };

  const handleRejectFinalTimeAndFinalize = () => {
    setFinalizeTimeDialog({ open: false, candidateId: '' });
    setLocalNotice({ severity: 'warning', message: 'Vor dem Finalisieren muss zuerst die korrekte Endzeit der Gruppe eingetragen werden.' });
  };

  const openUnlockDialogForCandidate = (candidateId) => {
    setUnlockDialog({ open: true, reason: '', candidateId: String(candidateId), reopenGroupTime: false });
  };

  const handleUnlock = async () => {
    if (!onUnlock || !unlockDialog.candidateId || !unlockDialog.reason.trim()) {
      return;
    }

    await onUnlock(stationCode, unlockDialog.reason.trim(), Number(unlockDialog.candidateId), { reopenGroupTime: unlockDialog.reopenGroupTime });
    setUnlockDialog({ open: false, reason: '', candidateId: '', reopenGroupTime: false });
  };

  const participantRows = selectedGroupParticipants.map((participant) => {
    const candidate = candidateById.get(String(participant.candidateId || '')) || null;
    const result = participant.outOfCompetition
      ? {
        ...buildEmptyGruppenpruefungResult(),
        candidateId: String(participant.candidateId || ''),
        outOfCompetition: true,
      }
      : {
        ...buildEmptyGruppenpruefungResult(),
        candidateId: String(participant.candidateId || ''),
        ...(stationState?.resultsByCandidateId?.[String(participant.candidateId || '')] || {}),
      };
    const actionAverage = participant.outOfCompetition ? null : getGruppenpruefungActionAverage(result, selectedGroup);
    const overallPoints = participant.outOfCompetition ? null : getGroupPracticalOverallPoints(result, selectedGroup, stationCode);
    const grade = participant.outOfCompetition ? null : getGroupPracticalGrade(overallPoints, stationCode);
    const failed = participant.outOfCompetition ? false : isGroupPracticalFailed(result, selectedGroup, stationCode);
    return {
      participant,
      candidate,
      result,
      actionAverage,
      overallPoints,
      grade,
      failed,
      contributions: (selectedGroup?.contributionEntries || []).filter(
        (entry) => String(entry.participantCandidateId || '') === String(participant.candidateId || ''),
      ),
    };
  });

  const scoredParticipantRows = participantRows.filter((row) => row.candidate && row.participant.outOfCompetition !== true);
  const groupTimeEntry = (selectedGroup?.timeEntries || [])[0] || null;
  const averageTimeMs = getGruppenpruefungAverageTimeMs(selectedGroup || {});
  const timePoints = getGroupPracticalTimePoints(averageTimeMs, stationCode);
  const groupTimeLocked = selectedGroup?.timeLock?.isFinal === true;
  const liveGroupTimeMs = groupTimerState.running && groupTimerState.startReference
    ? Math.max(0, timerTick - groupTimerState.startReference)
    : Number(groupTimeEntry?.totalMs || 0);
  const contributionTargetResult = stationState?.resultsByCandidateId?.[String(contributionDraft.participantCandidateId || '')] || null;
  const contributionTargetIsFinalized = String(contributionTargetResult?.status || '').trim() === 'finalized';
  const groupAssignmentHasChanges = (
    String(groupAssignmentDialog.leaderExaminerUserId || '') !== String(selectedGroup?.leaderExaminerUserId || '')
    || JSON.stringify((groupAssignmentDialog.participants || []).map((participant) => ({
      slotId: String(participant?.slotId || ''),
      candidateId: String(participant?.candidateId || ''),
      primaryExaminerUserId: String(participant?.primaryExaminerUserId || ''),
      outOfCompetition: participant?.outOfCompetition === true,
    }))) !== JSON.stringify((selectedGroupParticipants || []).map((participant) => ({
      slotId: String(participant?.slotId || ''),
      candidateId: String(participant?.candidateId || ''),
      primaryExaminerUserId: String(participant?.primaryExaminerUserId || ''),
      outOfCompetition: participant?.outOfCompetition === true,
    })))
  );
  const contributionEditorHasInvalidEntries = (contributionEditorDialog.entries || []).some(
    (entry) => !entry.examinerUserId || !entry.actionId || entry.points === '',
  );
  const safetyDescriptionTrimmed = String(safetyIncidentDialog.description || '').trim();

  if (!selectedGroup) {
    return <Alert severity="info">Für diesen Gruppenprüfbogen ist noch keine Gruppe vorbereitet.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              {isSheetFinalized && sheetRecord?.finalizedAt ? <Chip label={`Station vollständig finalisiert ${new Date(sheetRecord.finalizedAt).toLocaleString('de-DE')}`} color="success" variant="outlined" /> : null}
            </Stack>

            {selectedGroup?.safetyIncident?.hasIncident ? (
              <Alert severity="error">Sicherheitsrelevanter Fehler in {selectedGroup.label}. Die gesamte Gruppe ist automatisch nicht bestanden.</Alert>
            ) : null}
            {localNotice ? (
              <Alert severity={localNotice.severity}>{localNotice.message}</Alert>
            ) : null}

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{stationConfig.heading}</Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  xl: 'minmax(260px, 420px)',
                },
              }}
            >
              <Box>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Gruppe"
                  value={selectedGroup.id}
                  onChange={(event) => updateStation({ ...stationState, selectedGroupId: event.target.value })}
                  {...readableTextFieldProps}
                >
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>{group.label}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Aufgabenbeschreibung</Typography>
              {stationConfig.description.map((paragraph) => (
                <Typography key={paragraph} variant="body2" color="text.secondary">{paragraph}</Typography>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zeitpunkte</Typography>
              {stationConfig.timePointRules.map((rule) => (
                <Typography key={rule.label} variant="body2" color="text.secondary">{`${rule.label} = ${rule.points} Punkte`}</Typography>
              ))}
              <Typography variant="body2" color="error">{stationConfig.noTimeFailLabel}</Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notenschlüssel</Typography>
              {stationConfig.gradeRanges.map((range) => (
                <Typography key={range.grade} variant="body2" color={range.grade >= 5 ? 'error' : 'text.secondary'}>
                  {formatGroupGradeRangeLabel(range)}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zur Verfügung gestelltes Material</Typography>
          {stationConfig.material.map((item) => (
            <Typography key={item} variant="body2" color="text.secondary">• {item}</Typography>
          ))}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Gruppenbesetzung</Typography>
            <Button variant="outlined" onClick={handleOpenGroupAssignmentDialog} disabled={!canEditGroup}>Bearbeiten</Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Gruppenführer: {getSelectableUserName(examinerById.get(String(selectedGroup.leaderExaminerUserId || '')))}
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Position</TableCell>
                  <TableCell>Prüfling</TableCell>
                  <TableCell>Hauptprüfer</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {GRUPPENPRUEFUNG_PARTICIPANT_POSITIONS.map((position) => {
                  const participant = selectedGroupParticipants.find((entry) => entry.positionId === position.id) || null;
                  const candidate = candidateById.get(String(participant?.candidateId || ''));
                  return (
                    <TableRow key={position.id}>
                      <TableCell>{position.label}</TableCell>
                      <TableCell>{candidate?.fullName || '-'}</TableCell>
                      <TableCell>{getSelectableUserName(examinerById.get(String(participant?.primaryExaminerUserId || '')))}</TableCell>
                      <TableCell>{participant?.outOfCompetition ? 'Außer Wertung' : 'Wertung'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zeitwertung der Gruppe</Typography>
          <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatDuration(liveGroupTimeMs || averageTimeMs)}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`P Zeit: ${timePoints}`} variant="outlined" />
            {groupTimeLocked ? <Chip label="Zeit endgültig bestätigt" color="success" variant="outlined" /> : null}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={handleStartTimer} disabled={!canEditGroup || groupTimerState.running || groupTimeLocked}>Start</Button>
            <Button variant="outlined" onClick={handleStopTimer} disabled={!canEditGroup || !groupTimerState.running || groupTimeLocked}>Stopp</Button>
            <Button variant="outlined" onClick={openManualTimeDialog} disabled={!canEditGroup || groupTimeLocked}>Manuell eingeben</Button>
            <Button variant="outlined" onClick={handleResetGroupTime} disabled={!canEditGroup || groupTimeLocked}>Zeit zurücksetzen</Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} xl={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zwischenbewertung eines Prüfers</Typography>
              <TextField
                select
                fullWidth
                size="small"
                label="Prüfer"
                value={contributionDraft.examinerUserId}
                onChange={(event) => setContributionDraft((current) => ({ ...current, examinerUserId: String(event.target.value) }))}
                {...readableTextFieldProps}
              >
                <MenuItem value="">Auswählen</MenuItem>
                {examiners.map((examiner) => (
                  <MenuItem key={getSelectableUserId(examiner)} value={String(getSelectableUserId(examiner))}>{getSelectableUserName(examiner)}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Handlung"
                value={contributionDraft.actionId}
                onChange={(event) => setContributionDraft((current) => ({ ...current, actionId: String(event.target.value) }))}
                {...readableTextFieldProps}
              >
                <MenuItem value="">Auswählen</MenuItem>
                {(selectedGroup.actionCatalog || []).map((action) => (
                  <MenuItem key={action.id} value={action.id}>{action.label}</MenuItem>
                ))}
                <MenuItem value={GRUPPENPRUEFUNG_CUSTOM_ACTION_ID}>Individuelle Handlung anlegen…</MenuItem>
              </TextField>
              {contributionDraft.actionId === GRUPPENPRUEFUNG_CUSTOM_ACTION_ID ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Bezeichnung der individuellen Handlung"
                  value={customActionLabel}
                  onChange={(event) => setCustomActionLabel(event.target.value)}
                  {...readableTextFieldProps}
                />
              ) : null}
              <TextField
                select
                fullWidth
                size="small"
                label="Teilnehmer"
                value={contributionDraft.participantCandidateId}
                onChange={(event) => setContributionDraft((current) => ({ ...current, participantCandidateId: String(event.target.value) }))}
                {...readableTextFieldProps}
              >
                <MenuItem value="">Auswählen</MenuItem>
                {participantRows.filter((row) => row.candidate && row.participant.outOfCompetition !== true).map((row) => (
                  <MenuItem key={row.participant.slotId} value={String(row.candidate.id)}>{`${row.participant.positionLabel}: ${row.candidate.fullName}${row.participant.outOfCompetition ? ' (o.W.)' : ''}`}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Punkte"
                value={contributionDraft.points}
                onChange={(event) => setContributionDraft((current) => ({ ...current, points: event.target.value }))}
                {...readableTextFieldProps}
              >
                <MenuItem value="">Auswählen</MenuItem>
                {GRUPPENPRUEFUNG_POINT_OPTIONS.map((option) => (
                  <MenuItem key={option.points} value={option.points}>{getGruppenpruefungPointLabel(option.points)}</MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                label="Optionaler Hinweis"
                value={contributionDraft.notes}
                onChange={(event) => setContributionDraft((current) => ({ ...current, notes: event.target.value }))}
                multiline
                minRows={2}
                {...readableTextFieldProps}
              />
              {contributionTargetIsFinalized ? (
                <Alert severity="info">Der gewählte Teilnehmer ist bereits finalisiert. Für weitere Änderungen bitte zuerst den Teilnehmer entsperren.</Alert>
              ) : null}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="contained" onClick={handleAddContribution} disabled={!canEditGroup || contributionTargetIsFinalized}>Zwischenbewertung hinzufügen</Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} xl={8}>
          {scoredParticipantRows.length === 0 ? (
            <Alert severity="info">Für diese Gruppe gibt es aktuell keine Teilnehmer in Wertung.</Alert>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zwischenbewertungen bearbeiten</Typography>
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Stack spacing={1.25}>
                    {scoredParticipantRows.map((row) => {
                      const isParticipantFinalized = String(row.result.status || '').trim() === 'finalized';
                      return (
                        <Paper key={row.participant.slotId} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatCandidateTitleName(row.candidate?.fullName)}</Typography>
                            <Typography variant="body2" color="text.secondary">Position: {row.participant.positionLabel}</Typography>
                            <Typography variant="body2" color="text.secondary">Zwischenbewertungen: {row.contributions.length}</Typography>
                            <Typography variant="body2" color="text.secondary">Status: {isParticipantFinalized ? 'Finalisiert' : 'In Bearbeitung'}</Typography>
                            <Stack spacing={0.75}>
                              <Button size="small" variant="outlined" onClick={() => handleOpenContributionEditorDialog(row.candidate?.id)} disabled={!canEditGroup || isParticipantFinalized}>
                                Zwischenbewertungen bearbeiten
                              </Button>
                              <Button size="small" variant="contained" color="success" onClick={() => requestFinalizeCandidate(row.candidate?.id)} disabled={Boolean(actionBusy) || isOffline || !canEdit || isParticipantFinalized}>
                                {actionBusy === 'finalize' ? 'Finalisiert...' : 'Finalisieren'}
                              </Button>
                              <Button size="small" variant="text" color="warning" onClick={() => openUnlockDialogForCandidate(row.candidate?.id)} disabled={Boolean(actionBusy) || isOffline || !canEdit || !sheetRecord?.id || !isParticipantFinalized}>
                                Entsperren
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
                <Box sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Teilnehmer</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell>Zwischenbewertungen</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scoredParticipantRows.map((row) => {
                        const isParticipantFinalized = String(row.result.status || '').trim() === 'finalized';
                        return (
                          <TableRow key={row.participant.slotId}>
                            <TableCell>{formatCandidateTitleName(row.candidate?.fullName)}</TableCell>
                            <TableCell>{row.participant.positionLabel}</TableCell>
                            <TableCell>{row.contributions.length}</TableCell>
                            <TableCell>{isParticipantFinalized ? 'Finalisiert' : 'In Bearbeitung'}</TableCell>
                            <TableCell>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button size="small" variant="outlined" onClick={() => handleOpenContributionEditorDialog(row.candidate?.id)} disabled={!canEditGroup || isParticipantFinalized}>
                                  Zwischenbewertungen bearbeiten
                                </Button>
                                <Button size="small" variant="contained" color="success" onClick={() => requestFinalizeCandidate(row.candidate?.id)} disabled={Boolean(actionBusy) || isOffline || !canEdit || isParticipantFinalized}>
                                  {actionBusy === 'finalize' ? 'Finalisiert...' : 'Finalisieren'}
                                </Button>
                                <Button size="small" variant="text" color="warning" onClick={() => openUnlockDialogForCandidate(row.candidate?.id)} disabled={Boolean(actionBusy) || isOffline || !canEdit || !sheetRecord?.id || !isParticipantFinalized}>
                                  Entsperren
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Sicherheitsrelevanter Fehler</Typography>
          <Typography variant="body2" color="text.secondary">Liegt ein sicherheitsrelevanter Fehler vor?</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant={selectedGroup.safetyIncident?.answered === true && selectedGroup.safetyIncident?.hasIncident === true ? 'contained' : 'outlined'}
              color="error"
              onClick={handleOpenSafetyIncidentDialog}
              disabled={!canEditGroup}
            >
              Ja
            </Button>
            <Button
              variant={selectedGroup.safetyIncident?.answered === true && selectedGroup.safetyIncident?.hasIncident !== true ? 'contained' : 'outlined'}
              color="success"
              onClick={handleSaveSafetyIncidentNo}
              disabled={!canEditGroup}
            >
              Nein
            </Button>
          </Stack>
          {selectedGroup.safetyIncident?.answered !== true ? (
            <Alert severity="info">Vor dem Finalisieren des ersten Teilnehmers muss diese Frage mit Ja oder Nein beantwortet werden.</Alert>
          ) : null}
          {selectedGroup.safetyIncident?.hasIncident ? (
            <Alert severity="error">
              {selectedGroup.safetyIncident?.description || 'Sicherheitsrelevanter Fehler ohne Beschreibung gespeichert.'}
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Übersicht der Gruppe</Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Position</TableCell>
                    <TableCell>Teilnehmer</TableCell>
                    <TableCell>Hauptprüfer</TableCell>
                    <TableCell>Mittel Handlung</TableCell>
                    <TableCell>P Zeit</TableCell>
                    <TableCell>Gesamt</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participantRows.map((row) => (
                    <TableRow key={row.participant.slotId} selected={String(row.participant.candidateId || '') === String(selectedCandidateId || '')}>
                      <TableCell>{row.participant.positionLabel}</TableCell>
                      <TableCell>{row.candidate?.fullName || '-'}</TableCell>
                      <TableCell>{getSelectableUserName(examinerById.get(String(row.participant.primaryExaminerUserId || '')))}</TableCell>
                      <TableCell>{row.participant.outOfCompetition ? 'o.W.' : (row.actionAverage ?? '-')}</TableCell>
                      <TableCell>{row.participant.outOfCompetition ? 'o.W.' : timePoints}</TableCell>
                      <TableCell>{row.participant.outOfCompetition ? 'o.W.' : (row.overallPoints ?? '-')}</TableCell>
                      <TableCell>
                        {row.participant.outOfCompetition ? 'o.W.' : (
                          <Chip label={getGroupPracticalGradeLabel(row.grade, stationCode)} size="small" color={getGradeChipColor(row.grade, row.failed)} variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.participant.outOfCompetition ? 'Außer Wertung' : (String(row.result.status || '').trim() === 'finalized' ? 'Finalisiert' : 'In Bearbeitung')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={manualTimeDialog.open} onClose={closeManualTimeDialog} fullWidth maxWidth="xs">
        <DialogTitle>Zeit manuell eingeben</DialogTitle>
        <DialogContent>
          <TextField
            label="Zeit (MM:SS)"
            value={manualTimeDialog.value}
            onChange={(event) => setManualTimeDialog((current) => ({ ...current, value: event.target.value, error: '' }))}
            fullWidth
            margin="normal"
            error={Boolean(manualTimeDialog.error)}
            helperText={manualTimeDialog.error || 'Beispiel: 05:42'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeManualTimeDialog}>Abbrechen</Button>
          <Button variant="contained" onClick={confirmManualTimeDialog}>Übernehmen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={finalizeTimeDialog.open} onClose={() => setFinalizeTimeDialog({ open: false, candidateId: '' })} fullWidth maxWidth="sm">
        <DialogTitle>Endgültige Gruppenzeit bestätigen</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2">Soll die aktuell gestoppte bzw. eingetragene Gruppenzeit als endgültige Zeit für alle bewerteten Teilnehmer übernommen werden?</Typography>
            <Alert severity="info">Wenn du mit Ja bestätigst, wird die Zeit für alle bewerteten Teilnehmer übernommen und ist danach zunächst nicht mehr editierbar.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectFinalTimeAndFinalize}>Nein</Button>
          <Button variant="contained" color="success" onClick={handleConfirmFinalTimeAndFinalize}>Ja</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={safetyQuestionDialogOpen} onClose={() => setSafetyQuestionDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Sicherheitsfrage noch offen</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Alert severity="warning">Dieser Teilnehmer kann erst finalisiert werden, wenn die Frage nach einem sicherheitsrelevanten Fehler mit Ja oder Nein beantwortet wurde.</Alert>
            <Typography variant="body2">
              Bitte beantworten Sie im Bereich "Sicherheitsrelevanter Fehler", ob ein sicherheitsrelevanter Fehler vorliegt. Danach kann die Finalisierung erneut gestartet werden.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSafetyQuestionDialogOpen(false)}>Verstanden</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupAssignmentDialog.open} onClose={handleCloseGroupAssignmentDialog} fullWidth maxWidth="md">
        <DialogTitle>Gruppenbesetzung bearbeiten</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Gruppenführer"
              value={groupAssignmentDialog.leaderExaminerUserId}
              onChange={(event) => setGroupAssignmentDialog((current) => ({ ...current, leaderExaminerUserId: String(event.target.value) }))}
              helperText="Die Angabe dient der Dokumentation und beeinflusst die Bewertung nicht direkt."
              {...readableTextFieldProps}
            >
              <MenuItem value="">Auswählen</MenuItem>
              {examiners.map((examiner) => (
                <MenuItem key={getSelectableUserId(examiner)} value={String(getSelectableUserId(examiner))}>{getSelectableUserName(examiner)}</MenuItem>
              ))}
            </TextField>
            {GRUPPENPRUEFUNG_PARTICIPANT_POSITIONS.map((position) => {
              const participant = (groupAssignmentDialog.participants || []).find((entry) => entry.positionId === position.id) || null;
              return (
                <Box
                  key={position.id}
                  sx={{
                    display: 'grid',
                    gap: 1.25,
                    alignItems: 'center',
                    gridTemplateColumns: {
                      xs: '1fr',
                      xl: 'minmax(120px, 0.85fr) minmax(180px, 1.2fr) minmax(180px, 1.2fr) auto',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{position.label}</Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Prüfling"
                    value={participant?.candidateId || ''}
                    onChange={(event) => handleGroupAssignmentParticipantChange(participant?.slotId, { candidateId: String(event.target.value) })}
                    disabled={!participant}
                    {...readableTextFieldProps}
                  >
                    <MenuItem value="">Auswählen</MenuItem>
                    {candidates.map((candidate) => (
                      <MenuItem key={candidate.id} value={candidate.id}>{candidate.fullName}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Hauptprüfer"
                    value={participant?.primaryExaminerUserId || ''}
                    onChange={(event) => handleGroupAssignmentParticipantChange(participant?.slotId, { primaryExaminerUserId: Number(event.target.value) || '' })}
                    disabled={!participant}
                    {...readableTextFieldProps}
                  >
                    <MenuItem value="">Auswählen</MenuItem>
                    {examiners.map((examiner) => (
                      <MenuItem key={getSelectableUserId(examiner)} value={getSelectableUserId(examiner)}>{getSelectableUserName(examiner)}</MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    control={<Checkbox checked={participant?.outOfCompetition === true} onChange={(event) => handleGroupAssignmentParticipantChange(participant?.slotId, { outOfCompetition: event.target.checked })} disabled={!participant} />}
                    label="o.W."
                    sx={{ m: 0, minWidth: 'max-content' }}
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGroupAssignmentDialog}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveGroupAssignmentDialog} disabled={!canEditGroup || Boolean(actionBusy) || !groupAssignmentHasChanges}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={contributionEditorDialog.open} onClose={handleCloseContributionEditorDialog} fullWidth maxWidth="md">
        <DialogTitle>Zwischenbewertungen bearbeiten</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            <Alert severity="info">Änderungen werden erst mit Speichern übernommen.</Alert>
            {(contributionEditorDialog.entries || []).length === 0 ? (
              <Alert severity="info">Für diesen Teilnehmer liegen noch keine Zwischenbewertungen vor.</Alert>
            ) : null}
            {(contributionEditorDialog.entries || []).map((entry) => (
              <Paper key={entry.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Prüfer"
                    value={entry.examinerUserId}
                    onChange={(event) => handleContributionEditorEntryChange(entry.id, { examinerUserId: String(event.target.value) })}
                    {...readableTextFieldProps}
                  >
                    <MenuItem value="">Auswählen</MenuItem>
                    {examiners.map((examiner) => (
                      <MenuItem key={getSelectableUserId(examiner)} value={String(getSelectableUserId(examiner))}>{getSelectableUserName(examiner)}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Handlung"
                    value={entry.actionId || ''}
                    onChange={(event) => handleContributionEditorEntryChange(entry.id, { actionId: String(event.target.value) })}
                    {...readableTextFieldProps}
                  >
                    <MenuItem value="">Auswählen</MenuItem>
                    {(selectedGroup.actionCatalog || []).map((action) => (
                      <MenuItem key={action.id} value={action.id}>{action.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Punkte"
                    value={entry.points}
                    onChange={(event) => handleContributionEditorEntryChange(entry.id, { points: String(event.target.value) })}
                    {...readableTextFieldProps}
                  >
                    <MenuItem value="">Auswählen</MenuItem>
                    {GRUPPENPRUEFUNG_POINT_OPTIONS.map((option) => (
                      <MenuItem key={option.points} value={String(option.points)}>{getGruppenpruefungPointLabel(option.points)}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    label="Optionaler Hinweis"
                    value={entry.notes || ''}
                    onChange={(event) => handleContributionEditorEntryChange(entry.id, { notes: event.target.value })}
                    multiline
                    minRows={2}
                    {...readableTextFieldProps}
                  />
                  <Stack direction="row" justifyContent="flex-end">
                    <Button color="error" onClick={() => handleContributionEditorDeleteEntry(entry.id)}>Eintrag löschen</Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContributionEditorDialog}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveContributionEditorDialog} disabled={!canEditGroup || Boolean(actionBusy) || contributionEditorHasInvalidEntries}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={safetyIncidentDialog.open} onClose={handleCloseSafetyIncidentDialog} fullWidth maxWidth="sm">
        <DialogTitle>Sicherheitsrelevanter Fehler</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>Mit Speichern wird die Auswahl auf Ja gesetzt und der Fehler für die Gruppe dokumentiert.</Alert>
          <TextField
            label="Beschreibung des sicherheitsrelevanten Fehlers"
            value={safetyIncidentDialog.description}
            onChange={(event) => setSafetyIncidentDialog((current) => ({ ...current, description: event.target.value }))}
            multiline
            minRows={4}
            fullWidth
            margin="normal"
            autoFocus
            {...readableTextFieldProps}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSafetyIncidentDialog}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleSaveSafetyIncidentYes} disabled={!canEditGroup || Boolean(actionBusy) || !safetyDescriptionTrimmed}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unlockDialog.open} onClose={() => setUnlockDialog({ open: false, reason: '', candidateId: '', reopenGroupTime: false })} fullWidth maxWidth="sm">
        <DialogTitle>Teilnehmer entsperren</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            <Alert severity="warning">Beim Entsperren mit Zeitfreigabe kann eine geänderte Gruppenzeit Auswirkungen auf alle bewerteten Teilnehmer haben.</Alert>
          <TextField
            autoFocus
            label="Begründung"
            value={unlockDialog.reason}
            onChange={(event) => setUnlockDialog((current) => ({ ...current, reason: event.target.value }))}
            multiline
            minRows={3}
            fullWidth
            {...readableTextFieldProps}
          />
            <FormControlLabel
              control={<Checkbox checked={unlockDialog.reopenGroupTime === true} onChange={(event) => setUnlockDialog((current) => ({ ...current, reopenGroupTime: event.target.checked }))} />}
              label="Die Gruppenzeit muss angepasst werden und wieder editierbar sein."
              sx={{ m: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialog({ open: false, reason: '', candidateId: '', reopenGroupTime: false })}>Abbrechen</Button>
          <Button variant="contained" color="warning" onClick={handleUnlock} disabled={!unlockDialog.reason.trim() || Boolean(actionBusy)}>Entsperren</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}